// components/Dashboard/tabs/ProfileTab.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../../config';
import './ProfileTab.css';

const ProfileTab = ({
  user,
  statistics,
  automationRules,
  onChangePassword,
  onTabChange
}) => {
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [message, setMessage] = useState('');
  
  // Telegram state'leri
  const [telegramInfo, setTelegramInfo] = useState(null);
  const [telegramCode, setTelegramCode] = useState(null);
  const [telegramLoading, setTelegramLoading] = useState(false);

  const getAuthToken = () => localStorage.getItem('token');

  const getAxiosConfig = () => ({
    headers: {
      'Authorization': `Bearer ${getAuthToken()}`,
      'Content-Type': 'application/json'
    }
  });

  // Telegram bilgisini yükle
  useEffect(() => {
    fetchTelegramInfo();
  }, []);

  const fetchTelegramInfo = async () => {
    try {
      setTelegramLoading(true);
      const response = await axios.get(`${API_URL}/auth/my-telegram`, getAxiosConfig());
      setTelegramInfo(response.data.telegram);
    } catch (error) {
      console.error('Telegram bilgisi alinamadi:', error);
      setTelegramInfo(null);
    } finally {
      setTelegramLoading(false);
    }
  };

  const removeTelegramConnection = async () => {
    if (!window.confirm('Telegram baglantinizi kaldirmak istediginize emin misiniz? OTP kodlari artik bu hesaba gonderilmeyecek.')) return;
    
    try {
      setTelegramLoading(true);
      await axios.delete(`${API_URL}/auth/my-telegram`, getAxiosConfig());
      setMessage('Telegram baglantisi kaldirildi');
      setTelegramInfo({ connected: false });
      setTelegramCode(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Baglanti kaldirilamadi');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setTelegramLoading(false);
    }
  };

  const generateTelegramCode = async () => {
    try {
      setTelegramLoading(true);
      const response = await axios.post(`${API_URL}/auth/my-telegram/generate-code`, {}, getAxiosConfig());
      setTelegramCode(response.data);
      setMessage('Baglanti kodu olusturuldu');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      setMessage(error.response?.data?.error || 'Kod olusturulamadi');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setTelegramLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage('Yeni sifreler eslesmiyor!');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      setMessage('Sifre en az 6 karakter olmali!');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    
    const success = await onChangePassword(
      passwordForm.currentPassword, 
      passwordForm.newPassword
    );
    
    if (success) {
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setMessage('Sifre basariyla degistirildi!');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="profile-container">
      {message && (
        <div className={`profile-message ${message.includes('basari') || message.includes('olusturuldu') || message.includes('kaldirildi') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}

      {/* Profil Grid Layout */}
      <div className="profile-grid">
        {/* Sol Kolon - Kisisel Bilgiler */}
        <div className="profile-section">
          <div className="section-header">
            <h3>Kisisel Bilgiler</h3>
          </div>
          <div className="info-card">
            <div className="info-row">
              <div className="info-label">Kullanici Adi</div>
              <div className="info-value">{user.username}</div>
            </div>
            
            <div className="info-row">
              <div className="info-label">E-posta</div>
              <div className="info-value">{user.email || 'Belirtilmemis'}</div>
            </div>
            
            <div className="info-row">
              <div className="info-label">Yetki Seviyesi</div>
              <div className="info-value">
                <span className={`permission-badge ${user.role}`}>
                  {user.role === 'root' ? 'Tam Yetki' : 'Yonetici'}
                </span>
              </div>
            </div>
            
            <div className="info-row">
              <div className="info-label">Hesap Olusturma</div>
              <div className="info-value">
                {user.created_at ? 
                  new Date(user.created_at).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  }) : 
                  'Bilinmiyor'
                }
              </div>
            </div>
            
            <div className="info-row">
              <div className="info-label">Son Giris</div>
              <div className="info-value">
                {user.last_login ? 
                  new Date(user.last_login).toLocaleString('tr-TR') : 
                  'Ilk giris'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Sag Kolon - Guvenlik Ayarlari */}
        <div className="profile-section">
          <div className="section-header">
            <h3>Sifre Degistir</h3>
          </div>
          <div className="security-card">
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="form-field">
                <label>Mevcut Sifre</label>
                <div className="password-input-wrapper">
                  <input 
                    type={showPassword.current ? "text" : "password"}
                    name="currentPassword"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({
                      ...passwordForm, 
                      currentPassword: e.target.value
                    })}
                    required 
                    placeholder="Mevcut sifrenizi girin"
                    className="password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword({
                      ...showPassword, 
                      current: !showPassword.current
                    })}
                  >
                    {showPassword.current ? 'Gizle' : 'Goster'}
                  </button>
                </div>
              </div>
              
              <div className="form-field">
                <label>Yeni Sifre</label>
                <div className="password-input-wrapper">
                  <input 
                    type={showPassword.new ? "text" : "password"}
                    name="newPassword"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({
                      ...passwordForm, 
                      newPassword: e.target.value
                    })}
                    required 
                    minLength="6"
                    placeholder="Yeni sifrenizi girin"
                    className="password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword({
                      ...showPassword, 
                      new: !showPassword.new
                    })}
                  >
                    {showPassword.new ? 'Gizle' : 'Goster'}
                  </button>
                </div>
                <small className="field-hint">En az 6 karakter olmali</small>
              </div>
              
              <div className="form-field">
                <label>Yeni Sifre (Tekrar)</label>
                <div className="password-input-wrapper">
                  <input 
                    type={showPassword.confirm ? "text" : "password"}
                    name="confirmPassword"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({
                      ...passwordForm, 
                      confirmPassword: e.target.value
                    })}
                    required 
                    minLength="6"
                    placeholder="Yeni sifrenizi tekrar girin"
                    className="password-input"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword({
                      ...showPassword, 
                      confirm: !showPassword.confirm
                    })}
                  >
                    {showPassword.confirm ? 'Gizle' : 'Goster'}
                  </button>
                </div>
              </div>
              
              <button type="submit" className="update-password-btn">
                Sifreyi Guncelle
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Telegram Baglantisi Bolumu */}
      <div className="profile-section telegram-section">
        <div className="section-header">
          <h3>Telegram Baglantisi</h3>
        </div>
        <div className="telegram-card">
          <p className="telegram-description">
            OTP dogrulama kodlari Telegram hesabiniza gonderilir. Hesabinizi buradan yonetebilirsiniz.
          </p>

          {telegramLoading ? (
            <div className="telegram-loading">Yukleniyor...</div>
          ) : telegramInfo ? (
            <div className="telegram-content">
              <div className={`telegram-status-box ${telegramInfo.connected ? 'connected' : 'disconnected'}`}>
                <div className="status-header">
                  <span className={`status-indicator ${telegramInfo.connected ? 'online' : 'offline'}`}></span>
                  <span className="status-text">
                    {telegramInfo.connected ? 'Bagli' : 'Bagli Degil'}
                  </span>
                </div>
                
                {telegramInfo.connected && (
                  <div className="telegram-details">
                    <div className="detail-row">
                      <span className="detail-label">Telegram:</span>
                      <span className="detail-value telegram-username">@{telegramInfo.username}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Son Guncelleme:</span>
                      <span className="detail-value">{formatDate(telegramInfo.updated_at)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="telegram-actions">
                {telegramInfo.connected && (
                  <button 
                    onClick={removeTelegramConnection}
                    className="btn-telegram-disconnect"
                    disabled={telegramLoading}
                  >
                    Baglantiyi Kaldir
                  </button>
                )}
                
                <button 
                  onClick={generateTelegramCode}
                  className="btn-telegram-connect"
                  disabled={telegramLoading}
                >
                  {telegramInfo.connected ? 'Farkli Hesap Bagla' : 'Telegram Bagla'}
                </button>
              </div>

              {telegramCode && (
                <div className="telegram-code-section">
                  <h4>Baglanti Kodu</h4>
                  <div className="code-display">
                    <code>{telegramCode.code}</code>
                  </div>
                  <div className="code-instructions">
                    {telegramCode.instructions?.map((instruction, idx) => (
                      <p key={idx}>{instruction}</p>
                    ))}
                  </div>
                  <p className="code-expiry">
                    Kod gecerlilik: {formatDate(telegramCode.expiresAt)}
                  </p>
                  <button 
                    onClick={fetchTelegramInfo}
                    className="btn-refresh-status"
                  >
                    Baglanti Durumunu Kontrol Et
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="telegram-error">
              Telegram bilgisi alinamadi. 
              <button onClick={fetchTelegramInfo} className="btn-retry">Tekrar Dene</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;